import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, 
  FileText, 
  FilePlus, 
  Clock,
  ArrowRight,
  TrendingUp,
  Loader2,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { getCache, setCache, CACHE_KEYS } from "../lib/cache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_customers: 0,
    total_invoices: 0,
    draft_invoices: 0,
    completed_invoices: 0,
    recent_invoices: []
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [usingCache, setUsingCache] = useState(false);

  const fetchStats = useCallback(async (retryCount = 0, forceRefresh = false) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000;
    
    // Check cache first (unless force refreshing)
    if (!forceRefresh && retryCount === 0) {
      const cached = getCache(CACHE_KEYS.DASHBOARD_STATS);
      if (cached) {
        setStats(cached.data);
        setLoading(false);
        setUsingCache(true);
        
        // If data is fresh, don't fetch again
        if (cached.isFresh) {
          return;
        }
        // If stale, continue to fetch fresh data in background
      }
    }
    
    try {
      if (retryCount > 0 || forceRefresh) {
        setRefreshing(true);
      }
      
      const response = await axios.get(`${API}/dashboard/stats`, { timeout: 30000 });
      setStats(response.data);
      setCache(CACHE_KEYS.DASHBOARD_STATS, response.data);
      setUsingCache(false);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error(`Error fetching stats (attempt ${retryCount + 1}):`, error);
      
      // Retry on network errors or 5xx errors (cold start scenarios)
      if (retryCount < MAX_RETRIES && (
        error.code === 'ECONNABORTED' || 
        error.code === 'ERR_NETWORK' ||
        (error.response && error.response.status >= 500)
      )) {
        console.log(`Retrying in ${RETRY_DELAY * (retryCount + 1)}ms...`);
        setTimeout(() => {
          fetchStats(retryCount + 1, forceRefresh);
        }, RETRY_DELAY * (retryCount + 1));
        return;
      }
      
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      title: "Total Customers",
      value: stats.total_customers,
      icon: Users,
      color: "bg-blue-600",
      link: "/customers"
    },
    {
      title: "Total Invoices",
      value: stats.total_invoices,
      icon: FileText,
      color: "bg-emerald-600",
      link: "/invoices"
    },
    {
      title: "Estimates",
      value: stats.estimate_invoices,
      icon: FilePlus,
      color: "bg-amber-600",
      link: "/invoices?status=estimate"
    },
    {
      title: "Paid Invoices",
      value: stats.paid_invoices,
      icon: TrendingUp,
      color: "bg-violet-600",
      link: "/invoices?status=paid"
    }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex justify-between items-start mb-6 sm:mb-10">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 font-['Manrope'] tracking-tight">
            Dashboard
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1 sm:mt-2">
            Welcome to OP Road Service Invoice System
            {usingCache && !refreshing && (
              <span className="ml-2 text-xs text-amber-600">(cached)</span>
            )}
            {refreshing && (
              <span className="ml-2 inline-flex items-center text-blue-600 text-xs">
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Updating...
              </span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchStats(0, true)}
          disabled={refreshing}
          className="flex items-center gap-1"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-10">
        {statCards.map((stat, index) => (
          <Link key={stat.title} to={stat.link}>
            <Card 
              className="card-hover cursor-pointer border-slate-100 stagger-item"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wide">
                      {stat.title}
                    </p>
                    <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2 font-['Manrope']">
                      {loading ? "..." : stat.value}
                    </p>
                  </div>
                  <div className={`${stat.color} p-2 sm:p-3 rounded-xl text-white`}>
                    <stat.icon className="h-4 w-4 sm:h-6 sm:w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions & Recent Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
        {/* Quick Actions */}
        <Card className="border-slate-100" data-testid="quick-actions-card">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3">
            <CardTitle className="text-base sm:text-lg font-['Manrope']">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 sm:pt-3 space-y-2 sm:space-y-3">
            <Link to="/invoices/new" className="block">
              <Button 
                className="w-full justify-start gap-2 sm:gap-3 bg-blue-600 hover:bg-blue-700 text-white btn-animate text-sm sm:text-base"
                data-testid="quick-new-invoice-btn"
              >
                <FilePlus className="h-4 w-4 sm:h-5 sm:w-5" />
                Create New Invoice
              </Button>
            </Link>
            <Link to="/customers" className="block">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 sm:gap-3 btn-animate text-sm sm:text-base"
                data-testid="quick-manage-customers-btn"
              >
                <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                Manage Customers
              </Button>
            </Link>
            <Link to="/invoices" className="block">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 sm:gap-3 btn-animate text-sm sm:text-base"
                data-testid="quick-view-invoices-btn"
              >
                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                View All Invoices
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className="lg:col-span-2 border-slate-100" data-testid="recent-invoices-card">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base sm:text-lg font-['Manrope']">Recent Invoices</CardTitle>
            <Link to="/invoices">
              <Button variant="ghost" size="sm" className="gap-1 text-blue-600 text-xs sm:text-sm px-2 sm:px-3">
                View All
                <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 sm:pt-3">
            {loading ? (
              <div className="text-center py-6 sm:py-8 text-slate-500 text-sm sm:text-base">Loading...</div>
            ) : stats.recent_invoices.length === 0 ? (
              <div className="text-center py-6 sm:py-8">
                <Clock className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm sm:text-base">No invoices yet</p>
                <Link to="/invoices/new">
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-sm sm:text-base">
                    Create Your First Invoice
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {stats.recent_invoices.map((invoice, index) => (
                  <Link 
                    key={invoice.id} 
                    to={`/invoices/${invoice.id}`}
                    className="block"
                  >
                    <div 
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all duration-200 stagger-item gap-2 sm:gap-4"
                      style={{ animationDelay: `${index * 0.05}s` }}
                      data-testid={`recent-invoice-${invoice.id}`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="bg-slate-100 p-1.5 sm:p-2 rounded-lg">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm sm:text-base text-slate-900">
                            {invoice.invoice_number}
                          </p>
                          <p className="text-xs sm:text-sm text-slate-500">
                            {invoice.customer_name || "No customer"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 ml-9 sm:ml-0">
                        <Badge 
                          variant={invoice.status === "paid" ? "default" : "secondary"}
                          className={`text-xs ${
                            invoice.status === "paid" 
                              ? "bg-emerald-100 text-emerald-700" 
                              : invoice.status === "awaiting_payment" 
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {invoice.status === "paid" ? "Paid" : invoice.status === "estimate" ? "Estimate" : invoice.status === "awaiting_payment" ? "Awaiting Payment" : invoice.status}
                        </Badge>
                        <span className="text-xs sm:text-sm text-slate-500">
                          {formatDate(invoice.created_at)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
